/**
 * CashFlowDrillDownModal.tsx
 *
 * Drill-down modal for the Cash Flow Dashboard table.
 * Opens when a user clicks an Income / Expense / Balance cell and shows
 * the individual transactions that make up that aggregated figure.
 *
 * Layout notes
 * ─────────────
 * • Default width: 90 vw, capped at 1 280 px — takes up most of the viewport
 *   on every desktop resolution.
 * • Default height: 80 vh, capped at 88 vh.
 * • User-resizable: a drag handle in the bottom-left corner (RTL layout)
 *   lets the user pull the panel to any size they like.
 * • Table: table-auto + w-full so every column uses available space.
 *   Wrapper has overflow-x-auto as a safety net.
 */

import { useMemo, useRef, useCallback } from 'react';
import { X, TrendingUp, TrendingDown, BarChart2, Calendar, GripVertical } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

/** One rich row that feeds both the dashboard totals and the drill-down list. */
export interface DrillDownRow {
  id:            string;
  effectiveDate: string;            // YYYY-MM-DD
  description:   string;
  categoryName:  string | null;
  amount:        number;
  sourceType:    'Bank' | 'Known' | 'Recurring' | 'RolledOver';
  bankNickname:  string | null;
}

export type DrillDownFilter = 'income' | 'expense' | 'all';

export interface DrillDownTarget {
  /** ISO date string — start of the bucket (day / week / month) */
  bucketStart: string;
  /** ISO date string — end of the bucket (inclusive) */
  bucketEnd:   string;
  /** Human-readable label shown in the modal title, e.g. "ינו 2026" */
  label:       string;
  filter:      DrillDownFilter;
}

interface Props {
  open:    boolean;
  target:  DrillDownTarget | null;
  rows:    DrillDownRow[];   // ALL rows in the period — modal filters internally
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ILS2 = new Intl.NumberFormat('he-IL', {
  style:                 'currency',
  currency:              'ILS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function filterLabel(filter: DrillDownFilter): string {
  switch (filter) {
    case 'income':  return 'הכנסות';
    case 'expense': return 'הוצאות';
    default:        return 'כל התנועות';
  }
}

function sourceLabel(s: DrillDownRow['sourceType']): string {
  switch (s) {
    case 'Bank':       return 'בנק';
    case 'Known':      return 'פעולה ידועה';
    case 'Recurring':  return 'חוזרת';
    case 'RolledOver': return 'גלגול';
  }
}

function sourceBadgeClass(s: DrillDownRow['sourceType']): string {
  switch (s) {
    case 'Bank':       return 'bg-blue-100 text-blue-700';
    case 'Known':      return 'bg-purple-100 text-purple-700';
    case 'Recurring':  return 'bg-teal-100 text-teal-700';
    case 'RolledOver': return 'bg-amber-100 text-amber-700';
  }
}

// Format YYYY-MM-DD → DD/MM/YYYY
function fmtDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CashFlowDrillDownModal({ open, target, rows, onClose }: Props) {
  console.log('DEBUG: Modal Version 2.0 Loaded');
  // ── All hooks must be called unconditionally (before any early return) ──────

  const visibleRows = useMemo<DrillDownRow[]>(() => {
    if (!target) return [];
    return rows.filter(r => {
      const inBucket =
        r.effectiveDate >= target.bucketStart &&
        r.effectiveDate <= target.bucketEnd;
      if (!inBucket) return false;
      if (target.filter === 'income')  return r.amount > 0;
      if (target.filter === 'expense') return r.amount < 0;
      return true;
    });
  }, [rows, target]);

  const total = useMemo(
    () => visibleRows.reduce((s, r) => s + r.amount, 0),
    [visibleRows],
  );

  // ── Resize-drag: mouse-down on the grip → track mouse → update panel size ──
  const panelRef = useRef<HTMLDivElement>(null);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const panel = panelRef.current;
    if (!panel) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = panel.offsetWidth;
    const startH = panel.offsetHeight;

    const onMouseMove = (ev: MouseEvent) => {
      // RTL layout: grip is at the bottom-left corner.
      // Dragging LEFT (negative deltaX) makes the panel wider.
      const newW = Math.max(400, startW - (ev.clientX - startX));
      const newH = Math.max(300, startH + (ev.clientY - startY));
      panel.style.width  = `${newW}px`;
      panel.style.height = `${newH}px`;
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
  }, []);

  // ── Early return AFTER all hooks ────────────────────────────────────────────
  if (!open || !target) return null;

  const filterType = target.filter;

  const titleIcon =
    filterType === 'income'  ? <TrendingUp  className="w-5 h-5 text-green-600" /> :
    filterType === 'expense' ? <TrendingDown className="w-5 h-5 text-red-600"   /> :
                               <BarChart2    className="w-5 h-5 text-blue-600"  />;

  const titleColor =
    filterType === 'income'  ? 'text-green-700' :
    filterType === 'expense' ? 'text-red-700'   :
                               'text-blue-700';

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/*
        Modal panel
        ───────────
        • w-[90vw]            — 90 % of the viewport width by default
        • max-w-[1280px]      — hard cap on very wide monitors
        • min-w-[320px]       — never collapses below a readable width
        • style height:80vh   — sensible starting height
        • max-h-[88vh]        — never taller than the viewport
        • min-h-[300px]       — floor for drag-resize
      */}
      <div
        ref={panelRef}
        className="bg-white rounded-2xl shadow-2xl flex flex-col
          min-w-[320px] min-h-[300px]"
        style={{ width: '90%', height: '80vh', maxHeight: '88vh', resize: 'both', overflow: 'auto' }}
        dir="rtl"
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 flex items-start justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${
              filterType === 'income'  ? 'bg-green-50' :
              filterType === 'expense' ? 'bg-red-50'   :
                                         'bg-blue-50'
            }`}>
              {titleIcon}
            </div>
            <div>
              <h2 className={`text-lg font-bold ${titleColor}`}>
                {filterLabel(filterType)}
              </h2>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                <Calendar className="w-3.5 h-3.5" />
                {target.label}
                &nbsp;·&nbsp;
                {fmtDate(target.bucketStart)}
                {target.bucketStart !== target.bucketEnd && (
                  <> – {fmtDate(target.bucketEnd)}</>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Summary strip ──────────────────────────────────────────────── */}
        <div className={`flex-shrink-0 px-5 py-3 flex items-center justify-between text-sm border-b border-gray-100 ${
          filterType === 'income'  ? 'bg-green-50/60' :
          filterType === 'expense' ? 'bg-red-50/60'   :
                                     'bg-blue-50/60'
        }`}>
          <span className="text-gray-600 font-medium">
            {visibleRows.length} פעולות
          </span>
          <span className={`font-bold tabular-nums text-base ${
            total >= 0 ? 'text-green-700' : 'text-red-700'
          }`}>
            סה"כ: {ILS2.format(total)}
          </span>
        </div>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        {/*
          overflow-x-auto: horizontal scroll when content is wider than panel.
          overflow-y-auto: vertical scroll for long lists.
          flex-1 min-h-0:  fills remaining panel height without overflowing.
        */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
          {visibleRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <BarChart2 className="w-10 h-10 mb-3 text-gray-300" />
              <p className="text-sm font-medium">אין פעולות בתקופה זו</p>
            </div>
          ) : (
            /*
              table-auto: columns size to their content.
              w-full:     fills the full panel width when content is narrower.
            */
            <table className="w-full table-auto text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">
                    תאריך
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 w-full">
                    תיאור
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">
                    קטגוריה
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">
                    מקור
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 tabular-nums whitespace-nowrap">
                    חשבון בנק
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 tabular-nums whitespace-nowrap">
                    סכום
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visibleRows.map(row => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50/80 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap font-mono text-xs">
                      {fmtDate(row.effectiveDate)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-800" title={row.description}>
                      {row.description}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {row.categoryName ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                          {row.categoryName}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${sourceBadgeClass(row.sourceType)}`}>
                        {sourceLabel(row.sourceType)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                      {row.bankNickname ?? '—'}
                    </td>
                    <td className={`px-4 py-2.5 font-semibold tabular-nums whitespace-nowrap text-left ${
                      row.amount >= 0 ? 'text-green-700' : 'text-red-600'
                    }`}>
                      {ILS2.format(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer + resize grip ───────────────────────────────────────── */}
        <div className="relative flex-shrink-0 p-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
          >
            סגור
          </button>

          {/*
            Drag-to-resize grip.
            Sits at the bottom-left corner (RTL layout equivalent of bottom-right).
            onMouseDown kicks off the resize tracking loop above.
          */}
          <div
            onMouseDown={startResize}
            title="גרור לשינוי גודל"
            className="absolute bottom-1 left-1 p-1.5 cursor-nwse-resize
              text-gray-300 hover:text-gray-500 transition-colors select-none"
            aria-hidden="true"
          >
            <GripVertical className="w-4 h-4 rotate-45" />
          </div>
        </div>

      </div>
    </div>
  );
}
